import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success('Nachricht gesendet!', {
      description: 'Wir werden uns in Kürze bei Ihnen melden.',
    });
    setFormData({ name: '', email: '', phone: '', company: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-24 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Kontaktieren Sie uns
          </h2>
          <p className="text-lg text-muted-foreground">
            Haben Sie Fragen oder benötigen Sie ein Angebot? Wir freuen uns auf Ihre Nachricht!
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <div className="lg:col-span-2">
            <Card className="border-border shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Senden Sie uns eine Nachricht
                </CardTitle>
                <CardDescription>
                  Füllen Sie das Formular aus und wir melden uns schnellstmöglich bei Ihnen
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        placeholder="Ihr Name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="border-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company">Firma</Label>
                      <Input
                        id="company"
                        name="company"
                        placeholder="Ihre Firma"
                        value={formData.company}
                        onChange={handleChange}
                        className="border-input"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">E-Mail *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="ihre.email@beispiel.ch"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="border-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Telefon</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="078 123 45 67"
                        value={formData.phone}
                        onChange={handleChange}
                        className="border-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Nachricht *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Beschreiben Sie Ihr Anliegen..."
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="border-input resize-none"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                    size="lg"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Nachricht senden
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-xl flex items-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  <MapPin className="w-5 h-5 text-primary mr-2" />
                  Adresse
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-muted-foreground">
                <p className="font-semibold text-foreground">Pneu-Idris</p>
                <p>Hutmattstrasse 5</p>
                <p>5724 Dürrenäsch</p>
                <p className="text-sm pt-2">Aargau, Schweiz</p>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-xl flex items-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  <Phone className="w-5 h-5 text-primary mr-2" />
                  Kontakt
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Phone className="w-4 h-4 text-secondary" />
                  <a href="tel:+41788056066" className="text-muted-foreground hover:text-primary transition-colors">
                    078 805 60 66
                  </a>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="w-4 h-4 text-secondary" />
                  <a href="mailto:info@pneu-idris.ch" className="text-muted-foreground hover:text-primary transition-colors">
                    info@pneu-idris.ch
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border bg-primary/5">
              <CardHeader>
                <CardTitle className="text-xl flex items-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  <Clock className="w-5 h-5 text-primary mr-2" />
                  Öffnungszeiten
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Montag - Freitag:</span>
                  <span className="text-foreground font-medium">08:00 - 18:00</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Samstag:</span>
                  <span className="text-foreground font-medium">09:00 - 14:00</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Sonntag:</span>
                  <span className="text-foreground font-medium">Geschlossen</span>
                </div>
                <p className="text-xs text-muted-foreground pt-2">
                  * Termine außerhalb der Öffnungszeiten nach Vereinbarung möglich
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
